import { Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useRef } from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { LiveBackground } from "./motion";
import { HomeContent } from "../data/homeContent";

const MotionBox = motion(Box);

const scrollTo = (id: string) => {
  const section = document.getElementById(id);
  section?.scrollIntoView({ behavior: "smooth" });
};

type HeroProps = { hero: HomeContent["hero"] };

const Hero = ({ hero }: HeroProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  // Cursor-reactive glow: the radial highlight drifts toward the pointer,
  // spring-smoothed so it feels alive rather than snapping to the mouse.
  const pointerX = useMotionValue(0.5);
  const pointerY = useMotionValue(0.35);
  const glowX = useSpring(pointerX, { stiffness: 40, damping: 20 });
  const glowY = useSpring(pointerY, { stiffness: 40, damping: 20 });
  const glowLeft = useTransform(glowX, (v) => `${v * 100}%`);
  const glowTop = useTransform(glowY, (v) => `${v * 100}%`);

  const handlePointerMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    pointerX.set((e.clientX - rect.left) / rect.width);
    pointerY.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <Box
      id="home"
      ref={ref}
      as="section"
      position="relative"
      bg="chalk.100"
      minH={{ base: "auto", md: "92vh" }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      color="ink.900"
      textAlign="center"
      overflow="hidden"
      pt={{ base: 28, md: 16 }}
      pb={{ base: 20, md: 10 }}
      px={5}
      onMouseMove={handlePointerMove}
    >
      <LiveBackground variant="hero" />

      <MotionBox
        style={{ y: gridY }}
        position="absolute"
        inset={0}
        opacity={0.6}
        backgroundImage="radial-gradient(rgba(20, 37, 26, 0.14) 1px, transparent 1px)"
        backgroundSize="26px 26px"
        sx={{
          maskImage: "radial-gradient(ellipse 60% 60% at 50% 30%, black 40%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 60% 60% at 50% 30%, black 40%, transparent 100%)",
        }}
      />

      <MotionBox
        position="absolute"
        w="500px"
        h="500px"
        borderRadius="50%"
        bg="radial-gradient(closest-side, rgba(159, 226, 58, 0.4), transparent 70%)"
        style={{ left: glowLeft, top: glowTop, x: "-50%", y: "-50%" }}
        zIndex={0}
        pointerEvents="none"
      />

      <Flex direction="column" align="center" position="relative" zIndex={1} maxW="900px">
        <Heading
          as="h1"
          fontFamily="heading"
          fontSize={{ base: "4xl", sm: "5xl", md: "7xl" }}
          lineHeight="1.05"
          letterSpacing="-0.02em"
          mb={5}
        >
          {hero.headingStart}{" "}
          <Box as="span" color="lime.600">
            {hero.headingHighlight}
          </Box>
          .
        </Heading>

        <Text fontSize={{ base: "md", md: "xl" }} color="slate.700" maxW="620px" mb={10}>
          {hero.subtitle}
        </Text>

        <Flex gap={4} direction={{ base: "column", sm: "row" }}>
          <Button
            size="lg"
            bg="lime.500"
            color="ink.900"
            fontWeight="bold"
            px={8}
            _hover={{ bg: "lime.400", transform: "translateY(-2px)" }}
            transition="all 0.2s ease"
            onClick={() => scrollTo("products")}
          >
            {hero.ctaPrimary}
          </Button>
          <Button
            size="lg"
            variant="outline"
            borderColor="chalk.500"
            color="ink.900"
            px={8}
            _hover={{ borderColor: "lime.600", color: "lime.700", bg: "chalk.200" }}
            onClick={() => scrollTo("contacts")}
          >
            {hero.ctaSecondary}
          </Button>
        </Flex>
      </Flex>

      <MotionBox
        position="absolute"
        bottom={{ base: 6, md: 10 }}
        zIndex={1}
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      >
        <ChevronDownIcon boxSize={7} color="slate.500" />
      </MotionBox>
    </Box>
  );
};

export default Hero;
